// frontend/src/services/agent/AgentService.ts
/**
 * 🧠 Pensado por Alcélio Gomes - Codificado com Claude Code
 * 🚀 NeuroAgentes Platform - Intelligent Agent Development
 * 
 * AUDITORIA: Service principal para gerenciamento de agentes.
 * Orquestra operações usando HTTPClient, Mapper e Validator.
 */

import { Agent } from "../../types";
import { extractQr } from "../../utils/qr";
import { createLogger, executeWithErrorHandling } from "../../utils/logger";
import { AgentHTTPClient } from "./AgentHTTPClient";
import { AgentMapper, RawAgentData } from "./AgentMapper";
import { AgentValidator, ValidationResult, AgentValidationContext } from "./AgentValidator";

const logger = createLogger('AgentService');

/**
 * Service principal para operações de agentes
 */
class AgentService {
  private httpClient: AgentHTTPClient;

  constructor() {
    this.httpClient = new AgentHTTPClient();
  }

  /**
   * Busca todos os agentes de um projeto
   */
  async getAgents(projectId?: string): Promise<Agent[]> {
    return executeWithErrorHandling(
      async () => {
        const resolvedProjectId = this.resolveProjectId(projectId);
        logger.info('Buscando agentes', { projectId: resolvedProjectId });

        const rawAgents = await this.httpClient.getAgentsByProject(resolvedProjectId);
        const agents = AgentMapper.mapToAgentList(rawAgents as RawAgentData[], resolvedProjectId);

        logger.info(`${agents.length} agentes encontrados`);
        return agents;
      },
      logger,
      'buscar agentes'
    );
  }

  /**
   * Busca agente por ID
   */
  async getAgent(agentId: string): Promise<Agent> {
    return executeWithErrorHandling(
      async () => {
        logger.info('Buscando agente', { agentId });

        const rawAgent = await this.httpClient.getAgentById(agentId);
        const agent = AgentMapper.mapToAgent(rawAgent as RawAgentData);

        return agent;
      },
      logger,
      'buscar agente por ID'
    );
  }

  /**
   * Cria novo agente
   */
  async createAgent(agentData: Partial<Agent>, projectId?: string): Promise<Agent> {
    return executeWithErrorHandling(
      async () => {
        const resolvedProjectId = this.resolveProjectId(projectId);
        const context: AgentValidationContext = {
          isCreating: true,
          projectId: resolvedProjectId
        };

        logger.info('Criando agente', { projectId: resolvedProjectId });

        // Validar dados
        const validation = AgentValidator.validateAgent(agentData, context);
        if (!validation.isValid) {
          throw new Error(`Dados inválidos: ${validation.errors.join(', ')}`);
        }

        // Log de warnings se houver
        if (validation.warnings.length > 0) {
          logger.warn('Warnings na validação', { warnings: validation.warnings });
        }

        // Preparar dados para API
        const payload = AgentMapper.mapToAPIPayload({
          ...agentData,
          projetoId: resolvedProjectId,
          status: agentData.status || 'inativo'
        });

        const rawAgent = await this.httpClient.createAgent(payload);
        const agent = AgentMapper.mapToAgent(rawAgent as RawAgentData, resolvedProjectId);

        logger.info('Agente criado com sucesso', { agentId: agent.id });
        return agent;
      },
      logger,
      'criar agente'
    );
  }

  /**
   * Atualiza agente existente
   */
  async updateAgent(agentId: string, agentData: Partial<Agent>): Promise<Agent> {
    return executeWithErrorHandling(
      async () => {
        const context: AgentValidationContext = { isCreating: false };
        
        logger.info('Atualizando agente', { agentId });

        // Validar dados
        const validation = AgentValidator.validateAgent(agentData, context);
        if (!validation.isValid) {
          throw new Error(`Dados inválidos: ${validation.errors.join(', ')}`);
        }

        const payload = AgentMapper.mapToAPIPayload(agentData);
        const rawAgent = await this.httpClient.updateAgent(agentId, payload);
        const agent = AgentMapper.mapToAgent(rawAgent as RawAgentData);

        logger.info('Agente atualizado com sucesso');
        return agent;
      },
      logger,
      'atualizar agente'
    );
  }

  /**
   * Remove agente
   */
  async deleteAgent(agentId: string): Promise<void> {
    return executeWithErrorHandling(
      async () => {
        logger.info('Removendo agente', { agentId });

        // Buscar agente para validar remoção
        const agent = await this.httpClient.getAgentById(agentId);
        const mappedAgent = AgentMapper.mapToAgent(agent as RawAgentData);
        
        const validation = AgentValidator.validateDeletion(mappedAgent);
        if (!validation.isValid) {
          throw new Error(`Não é possível remover: ${validation.errors.join(', ')}`);
        }

        if (validation.warnings.length > 0) {
          logger.warn('Warnings na remoção', { warnings: validation.warnings });
        }

        await this.httpClient.deleteAgent(agentId);
        logger.info('Agente removido com sucesso');
      },
      logger,
      'remover agente'
    );
  }

  /**
   * Obtém QR Code para conexão
   */
  async getQRCode(agentId: string): Promise<string> {
    return executeWithErrorHandling(
      async () => {
        logger.info('Obtendo QR Code', { agentId });

        // Verificar se agente pode conectar
        const agent = await this.httpClient.getAgentById(agentId);
        const mappedAgent = AgentMapper.mapToAgent(agent as RawAgentData);
        
        const validation = AgentValidator.validateConnection(mappedAgent);
        if (!validation.isValid) {
          throw new Error(`Não é possível conectar: ${validation.errors.join(', ')}`);
        }

        const qrCodeData = await this.httpClient.getQRCode(agentId);
        const qrCode = extractQr(qrCodeData);

        if (!qrCode) {
          throw new Error('QR Code não pôde ser extraído dos dados recebidos');
        }

        logger.info('QR Code obtido com sucesso');
        return qrCode;
      },
      logger,
      'obter QR Code'
    );
  }

  /**
   * Verifica status de conexão
   */
  async getConnectionStatus(agentId: string): Promise<{ isConnected: boolean; status: string }> {
    return executeWithErrorHandling(
      async () => {
        logger.debug('Verificando status de conexão', { agentId });

        const status = await this.httpClient.getConnectionStatus(agentId);
        
        logger.debug('Status verificado', { isConnected: status.isConnected });
        return status;
      },
      logger,
      'verificar status de conexão'
    );
  }

  /**
   * Desconecta agente
   */
  async disconnectAgent(agentId: string): Promise<void> {
    return executeWithErrorHandling(
      async () => {
        logger.info('Desconectando agente', { agentId });

        // Implementar lógica de desconexão
        // Por enquanto, apenas log da operação
        logger.warn('Funcionalidade de desconexão ainda não implementada no backend');
      },
      logger,
      'desconectar agente'
    );
  }

  /**
   * Ativa agente (usa rota específica do backend)
   */
  async activateAgent(agentId: string): Promise<Agent> {
    return executeWithErrorHandling(
      async () => {
        logger.info('Ativando agente', { agentId });
        return this.httpClient.activateAgent(agentId);
      },
      logger,
      'ativar agente'
    );
  }

  /**
   * Desativa agente (usa rota específica do backend)
   */
  async deactivateAgent(agentId: string): Promise<Agent> {
    return executeWithErrorHandling(
      async () => {
        logger.info('Desativando agente', { agentId });
        return this.httpClient.deactivateAgent(agentId);
      },
      logger,
      'desativar agente'
    );
  }

  /**
   * Valida dados de agente
   */
  validateAgent(agentData: Partial<Agent>, context?: AgentValidationContext): ValidationResult {
    return AgentValidator.validateAgent(agentData, context || { isCreating: false });
  }

  // Métodos auxiliares privados

  private resolveProjectId(projectId?: string): string {
    if (projectId) return projectId;

    const savedProjectId = sessionStorage.getItem("selectedProjectId");
    if (savedProjectId) {
      logger.debug('Usando projeto da sessão', { projectId: savedProjectId });
      return savedProjectId;
    }

    throw new Error('ID do projeto não fornecido e não há projeto na sessão');
  }

  /**
   * Mapeia resposta da API para Agent (compatibilidade)
   * @deprecated Use AgentMapper.mapToAgent instead
   */
  mapResponseToAgent(rawData: Record<string, unknown>): Agent {
    logger.warn('Método mapResponseToAgent está deprecated, use AgentMapper.mapToAgent');
    return AgentMapper.mapToAgent(rawData as RawAgentData);
  }
}

// Export singleton instance
export const agentService = new AgentService();

// Export class for testing
export { AgentService };

export default agentService;